import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Hotel } from "lucide-react";
import { api, apiError, SERVICE_TYPES, VEHICLES } from "@/lib/api";
import { toLocalInput } from "@/components/fleet/RideSteps";
import { EscrowSeal } from "@/components/fleet/EscrowSeal";

const inTwoHours = () => toLocalInput(new Date(Date.now() + 2 * 3600000));

const Field = ({ label, children }) => (
  <label className="block"><span className="ff-label">{label}</span>{children}</label>
);

const Chips = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button key={o.value} type="button" data-testid={`${testid}-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} data-active={value === o.value} onClick={() => onChange(o.value)} className="ff-chip">{o.label.split(" • ")[0]}</button>
    ))}
  </div>
);

export default function ConciergeRequestPage({ code }) {
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ origin: "", destination: "", pickup_at: inTwoHours(), passengers: 1, guest_name: "", contact_phone: "", fare: "", service_type: "Transfer Aeroporto", vehicle_category: VEHICLES[0].value, notes: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    api.get(`/concierge/${code}`).then((r) => {
      setPartner(r.data);
      setForm((f) => ({ ...f, origin: r.data.partner_name }));
    }).catch((e) => setError(apiError(e, "QR non valido")));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/concierge/${code}/requests`, { ...form, fare: Number(form.fare), passengers: Number(form.passengers) });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err, "Invio non riuscito"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="concierge-page">
      <header className="ff-header h-16 flex items-center px-4"><span className="ff-brand">FLEETFLOW</span></header>
      <main className="max-w-lg mx-auto px-4 py-8 flex flex-col gap-6">
        {error && <p data-testid="concierge-error" className="text-red-400 font-semibold">{error}</p>}
        {partner && !done && (
          <>
            <div>
              <span className="ff-badge ff-badge-cobalt"><Hotel size={14} /> {partner.partner_name}</span>
              <h1 className="ff-display text-4xl font-extrabold mt-3">Richiedi un transfer</h1>
              <p className="text-[#94A3B8] mt-2">La richiesta arriva subito agli autisti NCC verificati della rete FLEETFLOW.</p>
            </div>
            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="concierge-form">
              <Field label="📍 Partenza"><input required minLength={2} data-testid="concierge-origin-input" className="ff-field" value={form.origin} onChange={set("origin")} /></Field>
              <Field label="🏁 Destinazione"><input required minLength={2} data-testid="concierge-destination-input" className="ff-field" placeholder="Es. Aeroporto di Pisa" value={form.destination} onChange={set("destination")} /></Field>
              <div className="grid grid-cols-[1fr_96px] gap-3">
                <Field label="Data e Ora"><input required type="datetime-local" data-testid="concierge-pickup-input" className="ff-field" value={form.pickup_at} onChange={set("pickup_at")} /></Field>
                <Field label="Persone"><input required type="number" min="1" max="19" data-testid="concierge-passengers-input" className="ff-field" value={form.passengers} onChange={set("passengers")} /></Field>
              </div>
              <Chips options={SERVICE_TYPES.filter((s) => s.value !== "Rientro a Vuoto")} value={form.service_type} onChange={set("service_type")} testid="concierge-service-chip" />
              <Chips options={VEHICLES} value={form.vehicle_category} onChange={set("vehicle_category")} testid="concierge-vehicle-chip" />
              <Field label="Nome ospite"><input required minLength={2} data-testid="concierge-guest-input" className="ff-field" value={form.guest_name} onChange={set("guest_name")} /></Field>
              <Field label="Telefono di contatto"><input required minLength={6} type="tel" data-testid="concierge-phone-input" className="ff-field" placeholder="+39 …" value={form.contact_phone} onChange={set("contact_phone")} /></Field>
              <Field label="Budget proposto (€)"><input required type="number" min="1" step="0.01" inputMode="decimal" data-testid="concierge-fare-input" className="ff-field text-2xl font-extrabold" placeholder="0,00" value={form.fare} onChange={set("fare")} /></Field>
              <Field label="Note (volo, bagagli…)"><input maxLength={300} data-testid="concierge-notes-input" className="ff-field" value={form.notes} onChange={set("notes")} /></Field>
              <button type="submit" disabled={saving} data-testid="concierge-submit-button" className="ff-btn-primary h-16 w-full uppercase text-base">{saving ? "Invio…" : "Invia richiesta transfer"}</button>
            </form>
            <EscrowSeal testid="concierge-escrow-seal" />
          </>
        )}
        {done && (
          <div data-testid="concierge-success" className="ff-card p-8 flex flex-col items-center text-center gap-4">
            <CheckCircle2 size={48} className="text-[#10B981]" />
            <h1 className="text-2xl font-extrabold">Richiesta inviata</h1>
            <p className="text-[#94A3B8]">Un autista NCC verificato prenderà in carico il transfer e contatterà il numero indicato.</p>
            <button type="button" data-testid="concierge-new-request-button" onClick={() => { setDone(false); setForm((f) => ({ ...f, destination: "", guest_name: "", contact_phone: "", fare: "", notes: "" })); }} className="ff-btn-secondary w-full">Nuova richiesta</button>
          </div>
        )}
      </main>
    </div>
  );
}
